import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import Login from "./Login";
import Agent from "./Agent";
import Home from "./Home";
import CalendarEventForm from "./Calender";
import EnvironmentPage from "./EnvironmentPage";
import ProfileSettingsPage from "./ProfileSettingsPage";
import HabcMonitorPage from "./HabcMonitorPage";
import RespiteRecordPage from "./RespiteRecordPage";
import AppShell from "./AppShell";
import FocusPage from "./FocusPage";
import SessionsPage from "./SessionsPage";

const App = () => {
  const [user, setUser] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(true);
  // Backend enrollment id, written by Login only after the study-team check
  // succeeds. Gating on it keeps the user on the login page until BOTH
  // Firebase auth and enrollment are confirmed, so a slow backend can't bounce
  // them into /home and straight back out.
  const [enrolledUserId, setEnrolledUserId] = useState(
    () => localStorage.getItem("userId") || ""
  );

  const syncEnrollment = useCallback(() => {
    setEnrolledUserId(localStorage.getItem("userId") || "");
  }, []);

  useEffect(() => {
    if (auth.currentUser) {
      setUser(auth.currentUser);
      setLoading(false);
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      (authUser) => {
        setUser(authUser);
        if (!authUser) {
          // Signed out: drop the enrollment id so a later sign-in must re-verify.
          setEnrolledUserId("");
        }
        setLoading(false);
      },
      (error) => {
        console.error("Auth error:", error);
        setLoading(false);
      }
    );
    // Fallback timeout in case onAuthStateChanged never fires
    const timeout = setTimeout(() => {
      setUser((prev) => prev || auth.currentUser);
      setLoading(false);
    }, 5000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  if (loading) return null;
  const isAuthenticated = Boolean((user || auth.currentUser) && enrolledUserId);

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <Login onEnrolled={syncEnrollment} />
            )
          }
        />
        {/* Every signed-in page renders inside the shared shell so the avatar
            stays on screen (and loaded) across navigation. */}
        <Route element={isAuthenticated ? <AppShell /> : <Navigate to="/" replace />}>
          <Route path="/chat" element={<Agent />} />
          <Route path="/environment" element={<EnvironmentPage />} />
          <Route path="/profile-settings" element={<ProfileSettingsPage />} />
          <Route path="/habc-monitor" element={<HabcMonitorPage />} />
          <Route path="/respite-record" element={<RespiteRecordPage />} />
          <Route path="/calender" element={<CalendarEventForm />} />
          <Route path="/home" element={<Home />} />
          <Route path="/focus" element={<FocusPage />} />
          <Route path="/sessions" element={<SessionsPage />} />
        </Route>

        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <Login onEnrolled={syncEnrollment} />
            )
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
